import { useEffect } from 'react'
import SwishLogo from '@/components/ui/SwishLogo'
import { t } from '@/lib/i18n'
import { processPayment } from '@/lib/payment'
import { fmt } from '@/lib/utils'
import { useKioskStore } from '@/stores/kiosk-store'

export default function CartScreen() {
  const cart = useKioskStore((s) => s.cart)
  const locale = useKioskStore((s) => s.locale)
  const updateCartCount = useKioskStore((s) => s.updateCartCount)
  const tipPercent = useKioskStore((s) => s.tipPercent)
  const setTipPercent = useKioskStore((s) => s.setTipPercent)
  const showsTip = useKioskStore((s) => s.showsTip)
  const setStep = useKioskStore((s) => s.setStep)
  const clearCart = useKioskStore((s) => s.clearCart)
  const remoteConfig = useKioskStore((s) => s.remoteConfig)
  const setCurrentCategoryLabel = useKioskStore((s) => s.setCurrentCategoryLabel)
  const setBackAction = useKioskStore((s) => s.setBackAction)
  const payMethod = useKioskStore((s) => s.payMethod)
  const setPayMethod = useKioskStore((s) => s.setPayMethod)
  const paymentMethods = useKioskStore((s) => s.paymentMethods)
  const showsStripeSwish = useKioskStore((s) => s.showsStripeSwish)
  // Swish-knapp visas om antingen (a) Stripe-integrerad Swish är påslagen
  // eller (b) det finns en standalone Swish-betalmetod på kassan.
  const hasStandaloneSwish = paymentMethods.some((m) => m.paymentProvider === 'Swish')
  const hasSwish = showsStripeSwish || hasStandaloneSwish
  const processing = useKioskStore((s) => s.processing)
  const parseToken = useKioskStore((s) => s.parseToken)
  const error = useKioskStore((s) => s.error)
  const orderMessage = useKioskStore((s) => s.orderMessage)
  const setOrderMessage = useKioskStore((s) => s.setOrderMessage)
  const sv = locale === 'sv'

  const hasStripe = paymentMethods.some((m) => m.paymentProvider === 'Stripe')

  // Use remote tip config if available, fall back to defaults
  const tipsEnabled = remoteConfig?.tipsEnabled ?? showsTip
  const tipPercentages = remoteConfig?.tipPercentages ?? [0, 5, 10, 15, 20]

  const totalAmount = cart.reduce((s, i) => s + i.price * i.count, 0)
  const tipAmount = Math.round((totalAmount * tipPercent) / 100)
  const grandTotal = totalAmount + tipAmount
  const isLandscape = remoteConfig?.layoutMode === 'landscape'

  // Wire landscape header: title + back action. Hides the internal orange bar.
  useEffect(() => {
    setCurrentCategoryLabel(sv ? 'VARUKORG' : 'CART')
    setBackAction(() => {
      setStep('browsing')
    })
    return () => {
      setCurrentCategoryLabel(null)
      setBackAction(null)
    }
  }, [sv, setCurrentCategoryLabel, setBackAction, setStep])

  return (
    <div className="h-full w-full flex flex-col items-center bg-kiosk-bg">
      <div className={`w-full flex flex-col h-full ${isLandscape ? 'max-w-4xl' : 'max-w-2xl'}`}>
        {/* Orange top bar removed — header shows VARUKORG + Tillbaka in both modes */}

        {/* Items */}
        <div className="flex-1 overflow-y-auto kiosk-scroll px-6 pb-4">
          {/* "Rensa"-knapp above items since the orange bar is gone in both modes */}
          {cart.length > 0 && (
            <div className="flex justify-end pt-4 pb-2">
              <button onClick={clearCart} className="kiosk-btn text-kiosk-muted text-xs font-semibold uppercase tracking-wider hover:text-red-400 transition-colors">
                {t('cart.clear', locale)}
              </button>
            </div>
          )}
          {cart.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <p className="text-kiosk-muted text-lg">{t('cart.empty', locale)}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {cart.map((item, idx) => (
                <div key={idx} className="bg-kiosk-surface rounded-2xl p-4 flex items-center gap-4">
                  {item.imageUrl && <img src={item.imageUrl} alt="" className="w-16 h-16 rounded-xl object-cover shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-kiosk-text font-semibold text-base">{item.name}</p>
                    {item.alternativesSummary && (
                      <p className="text-kiosk-muted text-sm mt-0.5">{item.alternatives.map((a) => a.alternativeItemName + (a.count > 1 ? ` x${a.count}` : '')).join(', ')}</p>
                    )}
                    {item.message && <p className="text-kiosk-accent/80 text-xs mt-1 italic">💬 &quot;{item.message}&quot;</p>}
                    <p className="text-kiosk-accent font-bold text-base mt-1">{fmt(item.price * item.count)}</p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <button
                      onClick={() => updateCartCount(idx, -1)}
                      className="kiosk-btn w-12 h-12 rounded-full bg-kiosk-surface-2 text-kiosk-text flex items-center justify-center text-2xl font-bold"
                    >
                      &minus;
                    </button>
                    <span className="text-kiosk-text text-xl font-bold w-8 text-center">{item.count}</span>
                    <button onClick={() => updateCartCount(idx, 1)} className="kiosk-btn w-12 h-12 rounded-full bg-kiosk-accent text-kiosk-bg flex items-center justify-center text-2xl font-bold">
                      +
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Order-level message — note from guest to the kitchen */}
          {cart.length > 0 && (
            <div className="mt-4 bg-kiosk-surface rounded-2xl p-5">
              <p className="text-kiosk-text font-semibold mb-1">
                {sv ? 'Meddelande till köket' : 'Note for the kitchen'}
                <span className="text-kiosk-muted font-normal ml-2 text-sm">({sv ? 'görs om möjligt' : 'done if possible'})</span>
              </p>
              <textarea
                value={orderMessage}
                onChange={(e) => setOrderMessage(e.target.value.slice(0, 300))}
                placeholder={sv ? 'T.ex. allergier, extra kryddigt, hämtas av någon annan...' : 'E.g. allergies, extra spicy, picked up by someone else...'}
                rows={2}
                className="w-full mt-2 bg-kiosk-bg border border-kiosk-surface-2 rounded-xl px-3 py-2 text-kiosk-text placeholder:text-kiosk-muted text-sm focus:outline-none focus:border-kiosk-accent/60 resize-none"
              />
            </div>
          )}

          {/* Tip selector */}
          {tipsEnabled && cart.length > 0 && (
            <div className="mt-6 bg-kiosk-surface rounded-2xl p-5">
              <p className="text-kiosk-text font-semibold mb-3">{t('cart.tip', locale)}</p>
              <div className="flex gap-2">
                {tipPercentages.map((pct) => (
                  <button
                    key={pct}
                    onClick={() => setTipPercent(pct)}
                    className={`kiosk-btn flex-1 py-3 rounded-xl text-base font-semibold ${tipPercent === pct ? 'bg-kiosk-accent text-kiosk-bg' : 'bg-kiosk-surface-2 text-kiosk-text'}`}
                  >
                    {pct}%
                  </button>
                ))}
              </div>
              {tipAmount > 0 && (
                <p className="text-kiosk-muted text-sm mt-2 text-right">
                  +{fmt(tipAmount)} {t('cart.tipAmount', locale)}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Bottom: total + pay button */}
        {cart.length > 0 && (
          <div className="shrink-0 px-6 pb-8 pt-4 bg-kiosk-surface border-t border-kiosk-surface-2 space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-kiosk-text text-xl font-semibold">{t('cart.total', locale)}</span>
              <span className="text-kiosk-accent text-2xl font-bold">{fmt(grandTotal)}</span>
            </div>
            {/* Inline payment selector (Card default + Swish). Clicking Betala skips CheckoutScreen. */}
            {hasStripe && (
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setPayMethod('card')}
                  className={`kiosk-btn flex-1 flex items-center justify-center gap-2 py-4 rounded-xl border-2 transition-all ${
                    payMethod === 'card' ? 'border-kiosk-accent bg-kiosk-accent/10' : 'border-kiosk-surface-2'
                  }`}
                >
                  <span className="text-2xl">💳</span>
                  <span className="text-kiosk-text font-semibold text-base">{t('checkout.card', locale)}</span>
                </button>
                {hasSwish && (
                  <button
                    onClick={() => setPayMethod('swish')}
                    className={`kiosk-btn flex-1 flex items-center justify-center gap-2 py-4 rounded-xl border-2 transition-all ${
                      payMethod === 'swish' ? 'border-kiosk-accent bg-kiosk-accent/10' : 'border-kiosk-surface-2'
                    }`}
                  >
                    <SwishLogo className="w-7 h-7" />
                    <span className="text-kiosk-text font-semibold text-base">Swish</span>
                  </button>
                )}
              </div>
            )}
            {error && <p className="text-red-400 text-sm text-center">{error}</p>}
            {!parseToken && (
              <p className="text-kiosk-accent/80 text-xs text-center">
                {sv ? 'Kassan behöver aktiveras. Håll in loggan i 5 sek för att logga in.' : 'The till needs to be activated. Press and hold the logo for 5 sec to log in.'}
              </p>
            )}
            <button
              onClick={() => void processPayment(locale)}
              disabled={processing || !parseToken}
              className="kiosk-btn w-full py-6 bg-kiosk-accent text-kiosk-bg font-bold rounded-2xl text-xl disabled:opacity-50"
            >
              {processing ? (
                <span className="flex items-center justify-center gap-3">
                  <div className="w-6 h-6 border-3 border-kiosk-bg/30 border-t-kiosk-bg rounded-full animate-spin" />
                  {t('checkout.processing', locale)}
                </span>
              ) : (
                `${t('checkout.payBtn', locale)} ${fmt(grandTotal)}`
              )}
            </button>
          </div>
        )}
      </div>
      {/* max-w-lg */}
    </div>
  )
}
